import { useContext, useEffect, useState } from "react";
import { BiPlus } from "react-icons/bi";
import { Helmet } from "react-helmet-async";
import { useOutletContext } from "react-router";
import { useProject } from "../../../utils/hooks/useProject";
import Input from "../../../utils/components/Input";
import API from "../../../utils/API";
import Modal from "../../../utils/components/Modal";
import Sets from "./Sets";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import { RequestContext } from "../../../utils/context/RequestContext";

const MetadataSets = () => {
  const [metadataSets, setMetadataSets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [metadataSetName, setMetadataSetName] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const { project } = useProject();

  const [translate] = useOutletContext();

  const { setRequestLoading, requestLoading } = useContext(RequestContext);

  useEffect(() => {
    if (project?.id)
      API.getData(`metadata_sets?project_id=${project.id}`).then(
        ({ data: res }) => {
          setTimeout(() => {
            const data = res.data.map((item) => {
              return {
                id: item.id,
                title: item.name,
                subTree: item.details,
                multi_select: item.multi_select,
              };
            });

            setMetadataSets(data);
          }, 20);

          setTimeout(() => {
            setIsLoading(false);
          }, 50);
        }
      );
    else setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (requestLoading) return;

    if (!metadataSetName.trim() || !project) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <AlertModal
            buttons={[
              {
                label: translate("okay"),
                onClick: onClose,
                classNames: "btn btn-primary",
              },
            ]}
            message={translate("fill_all_fields")}
          />
        ),
      });
      return false;
    }

    const body = {
      name: metadataSetName.trim(),
      project_id: project.id,
    };

    setRequestLoading(true);

    API.postData(`metadata_sets`, body).then(({ data: res }) => {
      const { data } = res;

      const newItem = {
        id: data.id,
        title: data.name.trim(),
        subTree: [],
        multi_select: false,
      };

      setMetadataSets([...metadataSets, newItem]);

      setShowModal(false);
      setMetadataSetName("");
    });
  };

  const handleUpdate = (id, body) => {
    if (requestLoading) return;

    setRequestLoading(true);
    API.putData(`metadata_set/${id}`, body).then(({ data: res }) => {
      const { data } = res;

      const newItem = {
        id: data.id,
        title: data.name.trim(),
        subTree: data.details,
        multi_select: data.multi_select,
      };

      const newSets = metadataSets.map((item) => {
        if (item.id === id) {
          return newItem;
        }

        return item;
      });

      setMetadataSets(newSets);
    });
  };

  const onDelete = (id) => {
    if (!id) return false;

    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                handleDelete(id, onClose);
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  const handleDelete = (id, closeModal) => {
    if (requestLoading) return;
    setRequestLoading(true);
    API.deleteData(`metadata_set/${id}`).then(() => {
      const newMetadataSets = metadataSets.filter((item) => item.id !== id);
      setMetadataSets(newMetadataSets);
      closeModal();
    });
  };

  const handleAddDetail = (parent_id, body, callback = null) => {
    if (requestLoading) return;

    if (parent_id && body) {
      body = {
        name: body.name,
      };

      setRequestLoading(true);

      API.postData(`metadata_set/${parent_id}/details`, body).then(
        ({ data: res }) => {
          const { data } = res;

          const newItem = {
            id: data.id,
            name: data.name,
            set_id: parent_id,
          };

          const sets = [...metadataSets];

          const index = sets.findIndex((item) => item.id === parent_id);

          if (index > -1) {
            sets[index].subTree = [...sets[index].subTree, newItem];
            setMetadataSets(sets);
          }

          if (callback) callback(newItem);
        }
      );
    }
  };

  return (
    <div className="MetadataSets SetsPage">
      <Helmet>
        <title>
          {translate("page_titles.project-settings-metadata-sets")} -{" "}
          {translate("page_titles.project-settings")}
        </title>
      </Helmet>
      <div className="row">
        <div className="col-12">
          <div
            className="d-flex align-items-center mb-4"
            role="button"
            onClick={() =>
              project?.id
                ? setShowModal(true)
                : alert(translate("select_a_project_alert"))
            }
          >
            <BiPlus className="fs-1 bg-primary text-white rounded-circle" />
            <span className="ms-2">
              {translate(
                "project_settings.metadata_sets.create_new_set_button"
              )}
            </span>
          </div>
        </div>
      </div>
      {project?.id ? (
        <>
          <div className="row">
            <div className="col-12">
              <Sets
                sets={metadataSets}
                setsLoading={isLoading}
                handleDelete={onDelete}
                handleAddDetail={handleAddDetail}
                type="METADATA_SET"
                handleUpdate={handleUpdate}
              />
            </div>
          </div>

          <Modal
            title={translate("project_settings.metadata_sets.create_new_set")}
            positiveButtonNode={translate("save")}
            negativeButtonNode={translate("cancel")}
            positiveButtonHandler={handleSubmit}
            modalSize="lg"
            show={showModal}
            setShow={(show) => {
              setShowModal(show);
            }}
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Input
                  type="text"
                  name="metadataSetName"
                  title={translate("project_settings.metadata_sets.set_name")}
                  placeholder={translate(
                    "project_settings.metadata_sets.set_name_placeholder"
                  )}
                  value={metadataSetName}
                  onChange={(e) => setMetadataSetName(e.target.value)}
                />
              </div>
            </form>
          </Modal>
        </>
      ) : (
        ``
      )}
    </div>
  );
};
export default MetadataSets;
